import React, { useMemo, useState } from 'react';
import { Text, StyleSheet } from 'react-native';
import AdminButton from '../../../Admin/components/AdminButton';
import AdminSelect from '../../../Admin/components/AdminSelect';
import { AdminFormField, AdminTextInput } from '../../../Admin/components/AdminFormField';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import ConfigDrawer from '../components/ConfigDrawer';
import { ConfigLocation, ScopedFormValues, ScopedNumbersConfig, ScopedSavePayload, ScopedValues } from '../configTypes';
import { isWholeNumber, locationKey, nationalKey } from '../configUtils';

interface ScopedNumbersDrawerProps {
  config: ScopedNumbersConfig;
  org: string;
  dept: string;
  mode: 'national' | 'location';
  /** Set when editing an existing override row; absent for "Add Override". */
  startLoc?: string;
  locations: ConfigLocation[];
  values: ScopedValues;
  conflict: boolean;
  onClose: () => void;
  onSave: (payload: ScopedSavePayload) => void;
}

/** Right side drawer for national values and location overrides (Cadence and Escalation). */
const ScopedNumbersDrawer: React.FC<ScopedNumbersDrawerProps> = ({
  config,
  org,
  dept,
  mode,
  startLoc,
  locations,
  values,
  onClose,
  onSave,
}) => {
  const nationalId = nationalKey(org, dept);
  const [loc, setLoc] = useState(startLoc ?? locations[0]?.name ?? '');
  const id = mode === 'national' ? nationalId : locationKey(loc, dept);

  const load = (rowId: string): ScopedFormValues =>
    Object.fromEntries(
      config.fields.map((f) => [f.key, values[rowId]?.[f.key] != null ? String(values[rowId][f.key]) : ''])
    );

  const [initial, setInitial] = useState<ScopedFormValues>(() => load(id));
  const [form, setForm] = useState<ScopedFormValues>(initial);

  const pickLocation = (name: string) => {
    const next = load(locationKey(name, dept));
    setLoc(name);
    setInitial(next);
    setForm(next);
  };

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);
  // Save stays disabled until every entered value is valid. A blank is only allowed in an override.
  const valid = config.fields.every((f) =>
    form[f.key] === '' ? mode === 'location' : isWholeNumber(form[f.key])
  );

  const title =
    mode === 'national'
      ? `Edit national ${config.noun}`
      : startLoc
        ? 'Edit location override'
        : 'Add location override';
  const subtitle = mode === 'national' ? `${org} / ${dept}, every location` : `${loc} / ${dept} (${org})`;

  return (
    <ConfigDrawer
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <>
          <AdminButton label="Cancel" variant="secondary" onPress={onClose} />
          <AdminButton
            label={config.saveLabel}
            variant="primary"
            disabled={!dirty || !valid}
            onPress={() => onSave({ id, loc, form, initial })}
          />
        </>
      }
    >
      {mode === 'location' && !startLoc ? (
        <AdminFormField label="Location">
          <AdminSelect
            value={loc}
            onChange={pickLocation}
            options={locations.map((l) => ({ value: l.name, label: l.name }))}
          />
        </AdminFormField>
      ) : null}
      {mode === 'location' ? (
        <Text style={styles.note}>Leave a value blank to keep the national {config.noun}.</Text>
      ) : null}
      {config.fields.map((f) => {
        const national = values[nationalId]?.[f.key];
        const invalid = form[f.key] !== '' && !isWholeNumber(form[f.key]);
        return (
          <AdminFormField
            key={f.key}
            label={f.label}
            hint={mode === 'national' ? f.hint : undefined}
            error={invalid ? 'Enter a whole number of 1 or more.' : undefined}
          >
            <AdminTextInput
              keyboardType="number-pad"
              value={form[f.key]}
              hasError={invalid}
              placeholder={mode === 'location' && national != null ? `National ${national}` : ''}
              onChangeText={(t) => setForm({ ...form, [f.key]: t.trim() })}
            />
            <Text style={styles.unit}>{f.unit}s</Text>
          </AdminFormField>
        );
      })}
      <Text style={styles.note}>{config.footnote}</Text>
    </ConfigDrawer>
  );
};

const styles = StyleSheet.create({
  note: {
    fontSize: 12,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
    marginBottom: 12,
  },
  unit: {
    fontSize: 11,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
  },
});

export default ScopedNumbersDrawer;
